import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import './HangmanGame.css';




const HangmanDrawing = ({ wrongGuesses, gameStatus }) => {
  return (
    <div className={`hangman-drawing ${gameStatus === 'won' ? 'won' : ''}`}>
      {wrongGuesses > 0 && <div className="hangman-base"></div>}
      {wrongGuesses > 1 && <div className="hangman-pole"></div>}
      {wrongGuesses > 2 && <div className="hangman-arm"></div>}
      {wrongGuesses > 3 && <div className="hangman-rope"></div>}
      {wrongGuesses > 4 && <div className="hangman-head"></div>}
      {wrongGuesses > 5 && <div className="hangman-body"></div>}
      {wrongGuesses > 6 && <div className="hangman-left-arm"></div>}
      {wrongGuesses > 7 && <div className="hangman-right-arm"></div>}
      {wrongGuesses > 8 && <div className="hangman-left-leg"></div>}
      {wrongGuesses > 9 && <div className="hangman-right-leg"></div>}
    </div>
  );
};

const WordDisplay = ({ word, guessedLetters, gameStatus, onDefinitionClick }) => {
  return (
    <div className="word-display">
      {word.split('').map((letter, index) => (
        <span key={index} className="word-letter">
          {guessedLetters.includes(letter) ? letter : '_'}
        </span>
      ))}
      {gameStatus === 'won' && (
        <span 
          className="dictionary-icon" 
          onClick={onDefinitionClick}
          role="button" 
          aria-label="Look up definition"
        >
          📖
        </span>
      )}
    </div>
  );
};

const Alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');

const LetterButtons = ({ guessedLetters, onLetterClick }) => {
  return (
    <div className="letter-buttons">
      {Alphabet.map((letter) => (
        <button
          key={letter}
          onClick={() => onLetterClick(letter)}
          disabled={guessedLetters.includes(letter)}
          className={guessedLetters.includes(letter) ? 'disabled' : ''}
        >
          {letter.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const HangmanGame = ({ wordList, soundEffects }) => {
  const [word, setWord] = useState('');
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  const [showDefinition, setShowDefinition] = useState(false);
  const [wordDefinition, setWordDefinition] = useState(null);
  const [definitionError, setDefinitionError] = useState(null);

  useEffect(() => {
    initializeGame();
  }, []);

  const initializeGame = () => {
    const randomWord = wordList[Math.floor(Math.random() * wordList.length)];
    setWord(randomWord);
    setGuessedLetters([]);
    setWrongGuesses(0);
    setGameStatus('playing');
    soundEffects.play('playButton');
  };

  const handleLetterClick = (letter) => {
    if (gameStatus !== 'playing') return;

    if (guessedLetters.includes(letter)) return;

    soundEffects.play('gameAction');

    const updatedGuessedLetters = [...guessedLetters, letter];
    setGuessedLetters(updatedGuessedLetters);

    if (word.includes(letter)) {
      // Correct guess
      soundEffects.play('goodMove');
      const allLettersGuessed = word
        .split('')
        .every((char) => updatedGuessedLetters.includes(char));

      if (allLettersGuessed) {
        setGameStatus('won');
        soundEffects.play('gameWon');
      }
    } else {
      // Incorrect guess
      const newWrongGuesses = wrongGuesses + 1;
      setWrongGuesses(newWrongGuesses);

      if (newWrongGuesses >= 10) {
        setGameStatus('lost');
        soundEffects.play('gameOver');
      }
    }
  };

  const handlePlayAgain = () => {
    soundEffects.play('playButton');
    initializeGame();
  };

  const fetchDefinition = async () => {
    try {
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
      if (!response.ok) {
        throw new Error('Definition not found');
      }
      const data = await response.json();
      setWordDefinition(data[0]);
      setDefinitionError(null);
      setShowDefinition(true);
    } catch (error) {
      setDefinitionError('Unable to find definition');
      setWordDefinition(null);
      setShowDefinition(true);
    }
  };

  const DefinitionModal = () => {
    if (!showDefinition) return null;
    
    return (
      <div 
        className="definition-modal"
        onClick={(e) => {
          if (e.target.className === 'definition-modal') {
            setShowDefinition(false);
          }
        }}
      >
        <div className="definition-content">
          <h3>{word} <span className="phonetic">{wordDefinition?.phonetic}</span></h3>
          {definitionError ? (
            <p className="error-message">{definitionError}</p>
          ) : (
            <>
              {wordDefinition?.meanings.map((meaning, index) => (
                <div key={index} className="meaning-section">
                  <p className="part-of-speech">{meaning.partOfSpeech}</p>
                  <ol>
                    {meaning.definitions.slice(0, 3).map((def, idx) => (
                      <li key={idx}>
                        <p>{def.definition}</p>
                        {def.example && <p className="example">"{def.example}"</p>}
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pre-hangman-game">
      {gameStatus === 'won' && (
        <>
          <Confetti />
          <div className="win-message">YOU WIN!</div>
          <div className="game-message">
            <button className="restart-button" onClick={handlePlayAgain}>
              Play Again
            </button>
          </div>
        </>
      )}
      <div className="hangman-game">
        <HangmanDrawing wrongGuesses={wrongGuesses} gameStatus={gameStatus} />

        <WordDisplay 
          word={word} 
          guessedLetters={guessedLetters} 
          gameStatus={gameStatus}
          onDefinitionClick={fetchDefinition}
        />

        {gameStatus === 'playing' && (
          <LetterButtons
            guessedLetters={guessedLetters}
            onLetterClick={handleLetterClick}
          />
        )}
      </div>
      <DefinitionModal />
      {gameStatus === 'lost' && (
        <div className="game-over">
          <h2>Game Over!</h2>
          <p>
            The word was "
            <span className="hangmanCorrectWord">{word}</span>
            "
            <span 
              className="dictionary-icon" 
              onClick={fetchDefinition}
              role="button" 
              aria-label="Look up definition"
            >
              📖
            </span>
          </p>
          <button className="restart-button" onClick={handlePlayAgain}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default HangmanGame;
